// The sign-in screens, in Voxowa's design (D-079): a mobile number and the 6-digit code sent to it by SMS, for
// everyone a company invited by phone; a work email for owners, which is also where a new company starts. A code
// goes only to a number the database allows (AUTH-14): this page asks our phone-sign-in function, never MSG91.
import {
  type ClipboardEvent,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import type { DemoApi, DemoRole } from "../lib/demoApi";
import { useSession } from "../app/session";
import { errorMessage } from "../app/ui";
import { cleanDigits, COUNTRIES, groupDigits, validMobile } from "../lib/phone";

const RESEND_SECONDS = 30;
const EMPTY_CODE = ["", "", "", "", "", ""];

function Mark({ size = 28, color = "#008049" }: { size?: number; color?: string }) {
  return (
    <svg width={Math.round(size * 1.11)} height={size} viewBox="4 2 58 52" aria-hidden="true">
      <path
        d="M8 34L23 49L46 20"
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="54.1" cy="9.9" r="6.5" fill={color} />
    </svg>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="login">
      <div className="login-main">
        <div className="login-brand">
          <Mark />
          <span>voxowa</span>
        </div>
        <div className="login-body">
          <div className="login-form">{children}</div>
        </div>
        <div className="login-foot">
          <a href="#">Privacy</a>
          <a href="#">Terms</a>
          <a href="#">Help</a>
        </div>
      </div>
      <aside className="login-panel" aria-hidden="true">
        <svg className="login-panel-mark" width="420" height="377" viewBox="4 2 58 52">
          <path
            d="M8 34L23 49L46 20"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="54.1" cy="9.9" r="6.5" fill="#FFFFFF" />
        </svg>
        <div />
        <div className="login-panel-copy">
          <div className="login-panel-title">No lead is ever lost.</div>
          <div className="login-panel-text">
            Your team's calls turn into follow-ups automatically, with a reminder for every callback.
          </div>
        </div>
      </aside>
    </div>
  );
}

function Heading({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="login-heading">
      <h1>{title}</h1>
      {children && <p>{children}</p>}
    </div>
  );
}

function Back({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="login-back" onClick={onClick}>
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M10 3L5 8L10 13"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {label}
    </button>
  );
}

/** Six boxes for a 6-digit code: typing moves on, backspace moves back, a pasted code fills them, the last digit sends. */
function CodeBoxes(
  { code, setCode, failed, onComplete, boxes }: {
    code: string[];
    setCode: (code: string[]) => void;
    failed: boolean;
    onComplete: (code: string[]) => void;
    boxes: MutableRefObject<(HTMLInputElement | null)[]>;
  },
) {
  const [focus, setFocus] = useState(0);

  const fill = (digits: string, start: number) => {
    const next = [...code];
    for (let k = 0; k < digits.length && start + k < 6; k++) next[start + k] = digits[k];
    setCode(next);
    boxes.current[Math.min(5, start + digits.length)]?.focus();
    if (next.every((d) => d)) onComplete(next);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e.key === "Backspace" && !code[i] && i > 0) {
      e.preventDefault();
      const next = [...code];
      next[i - 1] = "";
      setCode(next);
      boxes.current[i - 1]?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) boxes.current[i - 1]?.focus();
    else if (e.key === "ArrowRight" && i < 5) boxes.current[i + 1]?.focus();
    else if (e.key === "Enter" && code.every((d) => d)) onComplete(code);
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const digits = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (digits) {
      e.preventDefault();
      fill(digits, 0);
    }
  };

  return (
    <div className="login-code" onPaste={onPaste}>
      {code.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            boxes.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={i === 0 ? 6 : 1}
          aria-label={`Digit ${i + 1}`}
          aria-invalid={failed}
          className={`${failed ? "failed" : ""}${focus === i ? " focused" : ""}${d ? " filled" : ""}`}
          value={d}
          onFocus={() => setFocus(i)}
          onKeyDown={(e) => onKey(e, i)}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "");
            if (v.length > 1) {
              fill(v, i);
              return;
            }
            const next = [...code];
            next[i] = v.slice(-1);
            setCode(next);
            if (v && i < 5) boxes.current[i + 1]?.focus();
            if (next.every((x) => x)) onComplete(next);
          }}
        />
      ))}
    </div>
  );
}

/** The time left before a new code may be asked for, counting down from 30 seconds each time it starts. */
function useCountdown(): [number, () => void] {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return;
    const t = setTimeout(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return [left, () => setLeft(RESEND_SECONDS)];
}

/** "That code is not right" from the server reads as the design's wrong-code line; anything else as it came. */
function codeError(e: unknown): string {
  const message = errorMessage(e);
  return /not right|invalid|expired or is invalid|token has expired/i.test(message)
    ? "That code didn't match. Check the SMS and try again."
    : message;
}

export function SignIn() {
  const { api, reload } = useSession();
  const [step, setStep] = useState<"phone" | "otp" | "email" | "email-code" | "done">("phone");
  const [ci, setCi] = useState(0);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string[]>(EMPTY_CODE);
  const [codeFailed, setCodeFailed] = useState<string | null>(null);
  const [left, startCountdown] = useCountdown();
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const picker = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!picker.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (api.demo) return <DemoSignIn />;

  const country = COUNTRIES[ci];
  const valid = validMobile(country, phone);
  const showInvalid = touched && !focus && phone.length > 0 && !valid;
  const full = `${country.dial} ${groupDigits(phone, country.group)}`;
  const emailCode = step === "email-code";

  const toEmail = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setStep("email");
    setError(null);
    setTouched(false);
  };

  const toCode = () => {
    setCode(EMPTY_CODE);
    setCodeFailed(null);
    startCountdown();
    setTimeout(() => boxes.current[0]?.focus(), 60);
  };

  const sendPhone = async () => {
    if (!valid) {
      setTouched(true);
      setFocus(false);
      return;
    }
    if (busy) return;
    setBusy(true);
    setError(null);
    setOpen(false);
    try {
      await api.sendCode({ phone: `${country.dial}${phone}` });
      setStep("otp");
      toCode();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    const address = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address) || busy) {
      setTouched(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.sendCode({ email: address });
      setStep("email-code");
      toCode();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (digits: string[] = code) => {
    if (!digits.every((d) => d) || busy) return;
    setBusy(true);
    setCodeFailed(null);
    try {
      const target = emailCode ? { email: email.trim() } : { phone: `${country.dial}${phone}` };
      await api.verifyCode(target, digits.join(""));
      setStep("done");
      await reload();
    } catch (e) {
      setCodeFailed(codeError(e));
      setCode(EMPTY_CODE);
      setTimeout(() => boxes.current[0]?.focus(), 30);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setCodeFailed(null);
    setBusy(true);
    try {
      await api.sendCode(emailCode ? { email: email.trim() } : { phone: `${country.dial}${phone}` });
      toCode();
    } catch (e) {
      setCodeFailed(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (step === "done") {
    return (
      <Shell>
        <div className="login-done">
          <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
            <path
              d="M7 15.5L12.5 21L23 9.5"
              fill="none"
              stroke="#008049"
              strokeWidth="3.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <Heading title="You're signed in">Taking you to today's follow-ups…</Heading>
      </Shell>
    );
  }

  if (step === "otp" || step === "email-code") {
    const complete = code.every((d) => d);
    return (
      <Shell>
        <Back
          label={emailCode ? "Change email" : "Change number"}
          onClick={() => setStep(emailCode ? "email" : "phone")}
        />
        <Heading title="Enter the code">
          We sent a 6-digit code to <span className="login-strong">{emailCode ? email.trim() : full}</span>
        </Heading>
        <div className="login-field">
          <CodeBoxes
            code={code}
            setCode={(c) => {
              setCode(c);
              setCodeFailed(null);
            }}
            failed={codeFailed !== null}
            onComplete={(c) => void verify(c)}
            boxes={boxes}
          />
          {codeFailed && <div className="login-error" role="alert">{codeFailed}</div>}
        </div>
        <button
          type="button"
          className={`login-primary${complete ? "" : " idle"}${busy ? " busy" : ""}`}
          onClick={() => void verify()}
        >
          {busy ? "Checking…" : "Verify"}
        </button>
        <div className="login-resend">
          <span>Didn't get it?</span>
          {left > 0
            ? <span className="login-strong">Send again in 0:{String(left).padStart(2, "0")}</span>
            : (
              <button type="button" className="login-link" disabled={busy} onClick={() => void resend()}>
                Send again
              </button>
            )}
        </div>
      </Shell>
    );
  }

  if (step === "email") {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    return (
      <Shell>
        <Back
          label="Use a mobile number"
          onClick={() => {
            setStep("phone");
            setError(null);
            setTouched(false);
          }}
        />
        <Heading title="Sign in with email">
          Owners and managers sign in with their work email. New company? Sign in here and set it up next.
        </Heading>
        <div className="login-field">
          <label htmlFor="login-email">Work email</label>
          <div className={`login-input${touched && !emailValid && email ? " invalid" : ""}`}>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              autoFocus
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") void sendEmail();
              }}
            />
          </div>
          {error && <div className="login-error" role="alert">{error}</div>}
        </div>
        <button
          type="button"
          className={`login-primary${emailValid ? "" : " idle"}${busy ? " busy" : ""}`}
          onClick={() => void sendEmail()}
        >
          {busy ? "Sending code…" : "Send code"}
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <Heading title="Sign in">Enter your mobile number. We'll send you a 6-digit code by SMS.</Heading>
      <div className="login-field" ref={picker}>
        <label htmlFor="login-phone">Mobile number</label>
        <div className={`login-input${showInvalid || error ? " invalid" : focus ? " focused" : ""}`}>
          <button
            type="button"
            className="login-country"
            aria-label={`Country: ${country.name}`}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <span className="login-iso">{country.iso}</span>
            <span className="login-dial">{country.dial}</span>
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M2.5 4.5L6 8L9.5 4.5"
                fill="none"
                stroke="#6E665D"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <input
            id="login-phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder={country.placeholder}
            value={groupDigits(phone, country.group)}
            onChange={(e) => {
              setPhone(cleanDigits(e.target.value, country));
              setError(null);
            }}
            onFocus={() => setFocus(true)}
            onBlur={() => {
              setFocus(false);
              setTouched(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") void sendPhone();
            }}
          />
        </div>
        {open && (
          <div className="login-countries" role="listbox" aria-label="Country">
            {COUNTRIES.map((c, i) => (
              <button
                key={c.iso}
                type="button"
                role="option"
                aria-selected={i === ci}
                className={i === ci ? "on" : ""}
                onClick={() => {
                  setCi(i);
                  setOpen(false);
                  setPhone(phone.slice(0, c.len));
                }}
              >
                <span className="login-iso-plain">{c.iso}</span>
                <span className="login-country-name">{c.name}</span>
                <span className="login-country-dial">{c.dial}</span>
              </button>
            ))}
          </div>
        )}
        {showInvalid && !error && (
          <div className="login-error" role="alert">
            {country.iso === "IN" && phone.length === 10
              ? "Indian mobile numbers start with 6, 7, 8 or 9."
              : `Enter all ${country.len} digits of the number.`}
          </div>
        )}
        {error && <div className="login-error" role="alert">{error}</div>}
      </div>
      <button
        type="button"
        className={`login-primary${valid ? "" : " idle"}${busy ? " busy" : ""}`}
        onClick={() => void sendPhone()}
      >
        {busy ? "Sending code…" : "Send OTP"}
      </button>
      <p className="login-note">
        Only numbers invited by your company can sign in. New to Voxowa?{" "}
        <a href="#" onClick={toEmail}>Create a company account</a>
        <br />
        Owner or manager? <a href="#" onClick={toEmail}>Sign in with email</a>
      </p>
    </Shell>
  );
}

/** Demo mode signs in as one of the made-up company's people, without codes. */
function DemoSignIn() {
  const { api } = useSession();
  const demo = api as DemoApi;
  const roles: [DemoRole, string, string][] = [
    ["owner", "Owner", "Anil Sharma runs Sharma Realty"],
    ["solo", "Team of one", "Priya Nair on her own: owner and telecaller"],
    ["manager", "Manager", "Kavita Rao, the sales manager"],
    ["caller", "Telecaller", "Priya Nair, one of four telecallers"],
    ["platform", "Platform admin", "The platform's super admin"],
  ];
  return (
    <Shell>
      <Heading title="Try the demo">
        A made-up brokerage with a week of calls. Nothing is saved or sent anywhere.
      </Heading>
      <div className="login-demo">
        {roles.map(([role, label, who]) => (
          <button key={role} type="button" onClick={() => demo.signInAs(role)}>
            <strong>{label}</strong>
            <span>{who}</span>
          </button>
        ))}
      </div>
    </Shell>
  );
}
